import { useState } from 'react';
import { UserProfileDropdown } from './UserProfileDropdown';
import { CreditCard, Sun, Battery, Zap, Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { UserProfile } from '../types';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { BrandLogo } from './BrandLogo';

interface HeaderProps {
  profile?: UserProfile;
  onLogout?: () => void;
  onSettings?: () => void;
  onDashboard?: () => void;
}

export function Header({ profile, onLogout, onSettings, onDashboard }: HeaderProps) {
  const { currentTier } = useSubscriptionStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsMenuOpen, setSolutionsMenuOpen] = useState(false);
  
  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <a href="/" className="flex items-center">
            <div className="flex items-center">
              <BrandLogo className="h-9 w-auto" />
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
              Home
            </Link>
            <div className="relative group">
              <button 
                className="text-gray-700 hover:text-blue-600 font-medium flex items-center transition-colors"
                onClick={() => setSolutionsMenuOpen(!solutionsMenuOpen)}
              >
                Solutions
                <ChevronDown className="h-4 w-4 ml-1" />
              </button>
              {solutionsMenuOpen && (
                <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 flex items-center transition-colors">
                      <Sun className="h-4 w-4 mr-2 text-yellow-500" />
                      Solar Solutions
                    </a>
                    <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 flex items-center transition-colors">
                      <Battery className="h-4 w-4 mr-2 text-green-500" />
                      Battery Storage
                    </a>
                    <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 flex items-center transition-colors">
                      <Zap className="h-4 w-4 mr-2 text-blue-500" />
                      Hybrid Systems
                    </a>
                  </div>
                </div>
              )}
            </div>
            <Link to="/about-us" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
              About Us
            </Link>
            <Link to="/how-it-works" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
              How It Works
            </Link>
          </nav>

          <div className="flex items-center space-x-4">
            {profile && currentTier && (
              <Link 
                to="/subscription" 
                className="hidden sm:flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors"
              >
                <CreditCard className="h-4 w-4 mr-1" />
                {currentTier === 'free' ? 'Free Plan' : 
                 currentTier === 'business' ? 'Business Plan' : 
                 currentTier === 'enterprise' ? 'Enterprise Plan' : 'Ultimate Plan'}
              </Link>
            )}

            {profile && onLogout && (
              <UserProfileDropdown 
                profile={profile} 
                onLogout={onLogout}
                onSettings={onSettings}
                onDashboard={onDashboard}
              />
            )}
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden text-gray-700 hover:text-blue-600 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 border-t border-gray-200 pt-4">
            <div className="space-y-2">
              <Link 
                to="/" 
                className="block py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <div>
                <button 
                  className="flex items-center justify-between w-full py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                  onClick={() => setSolutionsMenuOpen(!solutionsMenuOpen)}
                >
                  <span>Solutions</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                {solutionsMenuOpen && (
                  <div className="pl-4 space-y-2 mt-2">
                    <a href="#" className="block py-2 text-gray-700 hover:text-blue-600 flex items-center transition-colors">
                      <Sun className="h-4 w-4 mr-2 text-yellow-500" />
                      Solar Solutions
                    </a>
                    <a href="#" className="block py-2 text-gray-700 hover:text-blue-600 flex items-center transition-colors">
                      <Battery className="h-4 w-4 mr-2 text-green-500" />
                      Battery Storage
                    </a>
                    <a href="#" className="block py-2 text-gray-700 hover:text-blue-600 flex items-center transition-colors">
                      <Zap className="h-4 w-4 mr-2 text-blue-500" />
                      Hybrid Systems
                    </a>
                  </div>
                )}
              </div>
              <Link 
                to="/about-us" 
                className="block py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                About Us
              </Link>
              <Link 
                to="/how-it-works" 
                className="block py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                How It Works
              </Link>
              
              {profile && currentTier && (
                <Link 
                  to="/subscription" 
                  className="block py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="flex items-center">
                    <CreditCard className="h-4 w-4 mr-2 text-blue-600" />
                    <span>Subscription: {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} Plan</span>
                  </div>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
